import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { C, S } from "../customers/theme";
import {
  getExpenses, getExpenseStats, createExpense, updateExpense, deleteExpense, computeVat,
  uploadReceipt, getReceiptUrl, clearReceipt, receiptIsPdf,
  EXPENSE_CATEGORIES, PAYMENT_METHODS, VAT_RATES,
} from "../lib/expenses";
import { todayISO } from "../lib/dates";
import { resizeImage } from "../products/imageResize";
import CameraCapture from "./CameraCapture";

const PAGE_SIZE = 25;
const money = (n) => `€${(Number(n) || 0).toFixed(2)}`;
const catLabel = (v) => EXPENSE_CATEGORIES.find((c) => c.value === v)?.label || (v || "other");

let _seq = 0;
function Field({ label, value, onChange, type = "text", options, placeholder, step, area, autoFocus }) {
  const id = useMemo(() => `ex-${++_seq}`, []);
  return (
    <div>
      <label htmlFor={id} style={S.label}>{label}</label>
      {options ? (
        <select id={id} style={S.input} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
          {options.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
        </select>
      ) : area ? (
        <textarea id={id} style={{ ...S.input, height: 54 }} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input id={id} style={S.input} type={type} step={step} value={value ?? ""} placeholder={placeholder} autoFocus={autoFocus} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

function ExpenseForm({ initial, onCancel, onSave }) {
  const seed = initial || {};
  const isEdit = !!seed.id;
  const [f, setF] = useState({
    expense_date: seed.expense_date || todayISO(),
    supplier: seed.supplier || "",
    description: seed.description || "",
    category: seed.category || "other",
    payment_method: seed.payment_method || "card",
    total_amount: seed.total_amount ?? "",
    vat_rate: seed.vat_rate ?? 23,
    vat_reclaimable: seed.vat_reclaimable ?? true,
    notes: seed.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const preview = computeVat(f.total_amount, f.vat_rate);

  // ── Receipt (CC4) ──────────────────────────────────────────────────────────
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);                 // pending receipt (uploaded on save)
  const [existingUrl, setExistingUrl] = useState(null);   // signed URL of an already-attached receipt
  const [hasExisting, setHasExisting] = useState(!!seed.receipt_url);
  const [showCamera, setShowCamera] = useState(false);
  const [receiptBusy, setReceiptBusy] = useState(false);
  const localPreview = useMemo(() => (file && file.type?.startsWith("image/") ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (localPreview) URL.revokeObjectURL(localPreview); }, [localPreview]);
  useEffect(() => {
    let alive = true;
    if (seed.id && seed.receipt_url) getReceiptUrl(seed).then((u) => { if (alive) setExistingUrl(u); }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const pickFile = () => fileRef.current?.click();
  const onPickFile = (e) => { const picked = e.target.files?.[0]; e.target.value = ""; if (picked) { setFile(picked); setHasExisting(false); } };
  const removeReceipt = async () => {
    if (file) { setFile(null); return; }
    if (seed.id && hasExisting) {
      setReceiptBusy(true);
      try { await clearReceipt(seed.id); setExistingUrl(null); setHasExisting(false); } catch (e) { setError(e.message); } finally { setReceiptBusy(false); }
    }
  };

  const submit = async () => {
    setError("");
    if (!f.expense_date) { setError("Date is required."); return; }
    setBusy(true);
    try {
      await onSave({ ...f, total_amount: f.total_amount === "" ? 0 : Number(f.total_amount), vat_rate: Number(f.vat_rate) }, file);
    } catch (e) { setError(e.message || "Could not save expense."); setBusy(false); }
  };

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div style={{ ...S.modalBox, width: 640 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            {isEdit ? "Edit Expense" : "Log Expense"}
            {seed.expense_number && <span style={{ color: C.textMuted, fontWeight: 500, fontSize: 13, marginLeft: 8, fontFamily: "monospace" }}>{seed.expense_number}</span>}
          </h2>
          <button onClick={onCancel} aria-label="Close" style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textMuted }}>✕</button>
        </div>
        <div style={S.grid2}>
          <Field label="Date *" type="date" value={f.expense_date} onChange={set("expense_date")} autoFocus />
          <Field label="Supplier" value={f.supplier} onChange={set("supplier")} placeholder="Who you paid" />
          <div style={{ gridColumn: "1 / span 2" }}><Field label="Description" value={f.description} onChange={set("description")} /></div>
          <Field label="Category" value={f.category} onChange={set("category")} options={EXPENSE_CATEGORIES} />
          <Field label="Payment method" value={f.payment_method} onChange={set("payment_method")} options={PAYMENT_METHODS} />
          <Field label="Total (incl. VAT) €" type="number" step="0.01" value={f.total_amount} onChange={set("total_amount")} placeholder="0.00" />
          <Field label="VAT rate" value={f.vat_rate} onChange={set("vat_rate")} options={VAT_RATES.map((r) => ({ value: r, label: `${r}%` }))} />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.text, cursor: "pointer", marginTop: 12 }}>
          <input type="checkbox" checked={!!f.vat_reclaimable} onChange={(e) => set("vat_reclaimable")(e.target.checked)} /> VAT is reclaimable
        </label>
        {Number(f.total_amount) > 0 && (
          <div style={{ background: C.surface2, borderRadius: 8, padding: 12, marginTop: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 13 }}><span style={{ color: C.textMuted }}>Excl. VAT</span><strong>{money(preview.subtotal)}</strong></div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}><span style={{ color: C.textMuted }}>VAT ({f.vat_rate}%)</span><strong>{money(preview.vat_amount)}</strong></div>
          </div>
        )}
        <div style={{ marginTop: 12 }}><Field label="Notes" area value={f.notes} onChange={set("notes")} /></div>

        <div style={{ marginTop: 14, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
          <label style={S.label}>Receipt</label>
          {(localPreview || (file && file.type === "application/pdf") || (hasExisting && existingUrl && !file)) ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              {file
                ? (file.type === "application/pdf"
                    ? <span style={{ fontSize: 13 }}>📄 {file.name}</span>
                    : <img src={localPreview} alt="Receipt preview" style={{ height: 72, borderRadius: 6, border: `1px solid ${C.border}` }} />)
                : (receiptIsPdf(seed.receipt_url)
                    ? <a href={existingUrl} target="_blank" rel="noreferrer" style={{ color: C.primary, fontSize: 13 }}>📄 View PDF receipt</a>
                    : <a href={existingUrl} target="_blank" rel="noreferrer"><img src={existingUrl} alt="Attached receipt" style={{ height: 72, borderRadius: 6, border: `1px solid ${C.border}` }} /></a>)}
              <button type="button" style={S.btn("secondary")} onClick={pickFile}>Replace</button>
              <button type="button" style={{ ...S.btn("ghost"), opacity: receiptBusy ? 0.6 : 1 }} disabled={receiptBusy} onClick={removeReceipt}>Remove</button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button type="button" style={S.btn("secondary")} onClick={() => setShowCamera(true)}>📷 Take Photo</button>
              <button type="button" style={S.btn("secondary")} onClick={pickFile}>⬆ Upload</button>
            </div>
          )}
          <input ref={fileRef} type="file" accept="image/*,application/pdf" style={{ display: "none" }} aria-label="Upload receipt" onChange={onPickFile} />
        </div>

        {error && <div style={{ color: C.danger, fontSize: 13, fontWeight: 600, marginTop: 12 }} role="alert">{error}</div>}
        <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
          <button type="button" style={S.btn("secondary")} onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" style={{ ...S.btn(), opacity: busy ? 0.6 : 1 }} onClick={submit} disabled={busy}>{busy ? "Saving…" : isEdit ? "Save Changes" : "Save Expense"}</button>
        </div>
      </div>
      {showCamera && <CameraCapture onCapture={(cf) => { setFile(cf); setHasExisting(false); setShowCamera(false); }} onClose={() => setShowCamera(false)} />}
    </div>
  );
}

export default function ExpenseManager({ autoNew, onAutoNew }) {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [stats, setStats] = useState({ totalExVat: 0, vatReclaimable: 0, totalIncVat: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [{ data, count: total }, s] = await Promise.all([
        getExpenses({ search, category: filterCat, page, pageSize: PAGE_SIZE }),
        getExpenseStats(),
      ]);
      setRows(data); setCount(total); setStats(s);
    } catch (e) { setError(e.message || "Could not load expenses."); }
    finally { setLoading(false); }
  }, [search, filterCat, page]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  useEffect(() => {
    if (autoNew) { setEditing({}); onAutoNew && onAutoNew(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoNew]);

  const save = async (payload, file) => {
    const exp = (editing && editing.id) ? await updateExpense(editing.id, payload) : await createExpense(payload);
    if (file) await uploadReceipt(exp.id, await resizeImage(file)); // resize passes PDFs through untouched
    setEditing(null);
    await load();
  };
  const del = async (e) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await deleteExpense(e.id); await load(); } catch (err) { setError(err.message); }
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const from = count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, count);
  const hasFilters = !!search || filterCat !== "all";

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 10 }}>
          <input style={S.searchBar} placeholder="Search expenses…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} aria-label="Search expenses" />
          <select style={{ ...S.input, width: "auto" }} value={filterCat} onChange={(e) => { setFilterCat(e.target.value); setPage(1); }} aria-label="Filter by category">
            <option value="all">All categories</option>
            {EXPENSE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <button style={S.btn()} onClick={() => setEditing({})}>+ Log Expense</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 20 }}>
        {[["Total (excl. VAT)", stats.totalExVat, C.primary], ["VAT Reclaimable", stats.vatReclaimable, C.warning], ["Total (incl. VAT)", stats.totalIncVat, C.textDim]].map(([label, val, color]) => (
          <div key={label} style={{ background: C.surface, border: `1px solid ${C.border}`, borderLeft: `3px solid ${color}`, borderRadius: 10, padding: "12px 16px" }}>
            <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 600 }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, marginTop: 2 }}>{money(val)}</div>
          </div>
        ))}
      </div>

      {error && (
        <div role="alert" style={{ display: "flex", alignItems: "center", gap: 12, background: C.dangerDim, border: `1px solid ${C.danger}`, color: C.text, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
          <span style={{ color: C.danger, fontWeight: 700 }}>⚠ {error}</span>
          <button style={S.btn("secondary")} onClick={() => setError("")}>Dismiss</button>
        </div>
      )}

      <div style={S.card}>
        <table style={S.table}>
          <thead><tr>
            {["Date", "Supplier", "Category", "Description", "Excl. VAT", "VAT", "Total", "Method", ""].map((h, i) => <th key={i} style={S.th}>{h}</th>)}
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={S.empty}>Loading…</td></tr>
            ) : count === 0 ? (
              <tr><td colSpan={9} style={S.empty}>
                {hasFilters ? (
                  <div>No expenses match your filters.<div style={{ marginTop: 10 }}><button style={S.btn("secondary")} onClick={() => { setSearch(""); setFilterCat("all"); setPage(1); }}>Clear filters</button></div></div>
                ) : (
                  <div><div style={{ fontWeight: 600, color: C.text, marginBottom: 4 }}>No expenses yet</div><div style={{ marginBottom: 12 }}>Log your purchases to track spend and reclaimable VAT.</div><button style={S.btn()} onClick={() => setEditing({})}>+ Log Expense</button></div>
                )}
              </td></tr>
            ) : rows.map((e) => (
              <tr key={e.id} style={{ cursor: "pointer" }} onClick={() => setEditing(e)}>
                <td style={S.td}>{e.expense_date || "—"}</td>
                <td style={S.td}><strong>{e.supplier || "—"}</strong>{e.receipt_url && <span title="Receipt attached" style={{ marginLeft: 6 }}>📎</span>}</td>
                <td style={S.td}><span style={S.pill(C.surface2)}>{catLabel(e.category)}</span></td>
                <td style={S.td}>{e.description || "—"}</td>
                <td style={S.td}>{money(e.subtotal)}</td>
                <td style={S.td}>{money(e.vat_amount)} {e.vat_reclaimable ? <span style={{ color: C.success, fontSize: 10 }}>✓</span> : <span style={{ color: C.danger, fontSize: 10 }}>✗</span>}</td>
                <td style={S.td}><strong>{money(e.total_amount)}</strong></td>
                <td style={S.td}>{(e.payment_method || "").replace(/_/g, " ")}</td>
                <td style={S.td} onClick={(ev) => ev.stopPropagation()}>
                  <button style={S.btn("ghost")} aria-label={`Delete expense ${e.expense_number || ""}`} onClick={() => del(e)}>🗑</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {count > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, flexWrap: "wrap", gap: 10 }}>
            <span style={{ color: C.textMuted, fontSize: 12 }}>Showing {from}–{to} of {count}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button style={{ ...S.btn("secondary"), opacity: page <= 1 ? 0.5 : 1 }} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Previous page">← Prev</button>
              <span style={{ color: C.textMuted, fontSize: 12 }}>Page {page} of {totalPages}</span>
              <button style={{ ...S.btn("secondary"), opacity: page >= totalPages ? 0.5 : 1 }} disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} aria-label="Next page">Next →</button>
            </div>
          </div>
        )}
      </div>

      {editing !== null && <ExpenseForm initial={editing} onCancel={() => setEditing(null)} onSave={save} />}
    </div>
  );
}
